import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { CognitiveBias } from '../../types';

interface BreadcrumbsProps {
  biases: CognitiveBias[];
  isLoading?: boolean;
}

// Separator component for better accessibility
const BreadcrumbSeparator = () => (
  <li aria-hidden="true" className="flex items-center">
    <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />
  </li>
);

const BreadcrumbsSkeleton = () => (
  <nav className="bg-orange-50/50 backdrop-blur-sm border-b border-orange-100">
    <div className="container mx-auto py-2 px-4">
      <ol 
        className="flex items-center text-sm py-2"
        aria-label="Breadcrumb"
      >
        <li>
          <div className="h-5 w-12 bg-orange-100/30 animate-pulse rounded" />
        </li>
        <BreadcrumbSeparator />
        <li>
          <div className="h-5 w-16 bg-orange-100/30 animate-pulse rounded" />
        </li>
        <BreadcrumbSeparator />
        <li>
          <div className="h-5 w-24 bg-orange-100/30 animate-pulse rounded" />
        </li>
      </ol>
    </div>
  </nav>
);

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ biases }) => {
  const location = useLocation();
  const isLoading = !biases || biases.length === 0;
  
  if (isLoading) {
    return <BreadcrumbsSkeleton />;
  }

  const pathnames = location.pathname.split('/').filter(x => x);
  
  if (pathnames.length === 0) return null;

  let breadcrumbs = pathnames.map((value, index) => {
    const to = `/${pathnames.slice(0, index + 1).join('/')}`;
    const isLast = index === pathnames.length - 1;
    
    // If this is a bias page
    if (pathnames[0] === "bias" && index === 1) {
      const biasId = value;
      const bias = biases?.find(b => b?.id?.toString() === biasId);
      if (!bias) {
        return (
          <React.Fragment key={to}>
            <BreadcrumbSeparator />
            <li>
              <div className="h-5 w-32 bg-orange-200/50 animate-pulse rounded" />
            </li>
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={to}>
          <BreadcrumbSeparator />
          <li>
            <Link
              to="/learn/"
              className="text-orange-500 hover:text-orange-600 transition-colors"
            >
              Learn
            </Link>
          </li>
          <BreadcrumbSeparator />
          <li>
            <Link
              to={`/category/${bias.category.toLowerCase()}/`}
              className="text-orange-500 hover:text-orange-600 transition-colors"
            >
              {bias.category} Biases
            </Link>
          </li>
          <BreadcrumbSeparator />
          <li>
            <span aria-current="page">{bias.name}</span>
          </li>
        </React.Fragment>
      );
    }

    // If this is a category page
    if (pathnames[0] === "category" && index === 1) {
      return (
        <React.Fragment key={to}>
          <BreadcrumbSeparator />
          <li>
            <Link
              to="/learn/"
              className="text-orange-500 hover:text-orange-600 transition-colors"
            >
              Learn
            </Link>
          </li>
          <BreadcrumbSeparator />
          <li>
            <span aria-current="page">
              {value.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} Biases
            </span>
          </li>
        </React.Fragment>
      );
    }

    // Skip "category" and "bias" in the path
    if (value === "category" || value === "bias") {
      return null;
    }

    // Default breadcrumb rendering
    const label = value
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    // Ensure trailing slash on all internal links
    const linkPath = to.endsWith('/') ? to : `${to}/`;

    return (
      <React.Fragment key={to}>
        <BreadcrumbSeparator />
        <li>
          {isLast ? (
            <span aria-current="page">{label}</span>
          ) : (
            <Link
              to={linkPath}
              className="text-orange-500 hover:text-orange-600 transition-colors"
            >
              {label}
            </Link>
          )}
        </li>
      </React.Fragment>
    );
  }).filter(Boolean);

  if (location.pathname === '/biases' || location.pathname === '/biases/') {
    breadcrumbs = [
      <React.Fragment key="learn">
        <BreadcrumbSeparator />
        <li>
          <Link
            to="/learn/"
            className="text-orange-500 hover:text-orange-600 transition-colors"
          >
            Learn
          </Link>
        </li>
        <BreadcrumbSeparator />
        <li>
          <span aria-current="page">All Biases</span>
        </li>
      </React.Fragment>
    ];
  }

  return (
    <nav aria-label="Breadcrumb" className="bg-orange-50/50 backdrop-blur-sm border-b border-orange-100">
      <div className="container mx-auto py-2 px-4">
        <ol className="flex items-center text-sm py-2">
          <li>
            <Link
              to="/"
              className="text-orange-500 hover:text-orange-600 transition-colors"
            >
              Home
            </Link>
          </li>
          {breadcrumbs}
        </ol>
      </div>
    </nav>
  );
}; 